import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TripReportForm from './TripReportForm';
import TripReportCard from './TripReportCard';

interface TripReport {
  id: number;
  country_code: string;
  city?: string;
  title: string;
  content: string;
  experience_rating: number;
  tags: string[];
  upvotes: number;
  created_at: string;
}

interface TripReportListProps {
  countryCode: string;
}

export default function TripReportList({ countryCode }: TripReportListProps) {
  const [reports, setReports] = useState<TripReport[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchReports();
  }, [countryCode]);

  const fetchReports = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/trip-reports/${countryCode}?limit=10`);
      if (!response.ok) throw new Error('Failed to fetch reports');
      const data = await response.json();
      setReports(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleUpvote = async (id: number) => {
    try {
      const response = await fetch(`/api/trip-reports/${id}/upvote`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Upvote failed');
      const { upvotes } = await response.json();
      setReports(reports.map(r => r.id === id ? { ...r, upvotes } : r));
    } catch (e) {
      console.error('Upvote error:', e);
    }
  };

  const handleReport = async (id: number, reason: string) => {
    try {
      const response = await fetch(`/api/trip-reports/${id}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      if (!response.ok) throw new Error('Report failed');
    } catch (e) {
      console.error('Report error:', e);
    }
  };

  const handleReportSubmitted = () => {
    setShowForm(false);
    fetchReports();
  };

  return (
    <div className="space-y-4">
      {/* Form Toggle Button */}
      {!showForm && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setShowForm(true)}
          className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-cannabis-600 to-cannabis-700 hover:from-cannabis-700 hover:to-cannabis-800 text-white font-bold text-sm transition-all shadow-lg hover:shadow-xl"
        >
          ✍️ Share Your Trip Report
        </motion.button>
      )}

      {/* Form */}
      <AnimatePresence>
        {showForm && (
          <TripReportForm
            countryCode={countryCode}
            onSubmit={handleReportSubmitted}
            onCancel={() => setShowForm(false)}
          />
        )}
      </AnimatePresence>

      {/* Reports List */}
      <div className="space-y-3">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="text-white/50 text-sm">Loading trip reports...</div>
          </div>
        )}

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm">
            {error}
          </div>
        )}

        {!loading && reports.length === 0 && !error && (
          <div className="text-center py-8">
            <p className="text-white/40 text-sm">No trip reports yet. Be the first to share!</p>
          </div>
        )}

        {reports.map(report => (
          <TripReportCard
            key={report.id}
            report={report}
            onUpvote={handleUpvote}
            onReport={handleReport}
          />
        ))}
      </div>
    </div>
  );
}
